import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Calendar, ChevronRight, Eye, EyeOff, Users } from 'lucide-react';
import { Produksjon } from '../../services/api';

interface ProductionCardProps {
  produksjon: Produksjon;
  onClick: () => void;
}

const ProductionCard: React.FC<ProductionCardProps> = ({ produksjon, onClick }) => {
  const formatDato = (tidString: string) => {
    const dato = new Date(tidString);
    return dato.toLocaleDateString('nb-NO', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatKortDato = (tidString: string) => {
    const dato = new Date(tidString);
    return dato.toLocaleDateString('nb-NO', {
      day: 'numeric',
      month: 'short',
    });
  };

  // Gradient-farge basert på publisert status
  const gradientColor = produksjon.publisert 
    ? 'from-green-500 to-emerald-400' 
    : 'from-amber-500 to-orange-400';

  return (
    <Card 
      className="cursor-pointer hover:shadow-lg transition-all hover:scale-[1.01] overflow-hidden"
      onClick={onClick}
    >
      <div className={`h-1 bg-gradient-to-r ${gradientColor}`} />
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-start gap-2 sm:gap-3">
          {/* Avatar med dato (skjules på tiny screens, vises på xs+) */}
          <Avatar className="h-10 w-10 sm:h-12 sm:w-12 shrink-0 hidden xs:flex">
            <AvatarFallback className="bg-primary/10 text-primary">
              <Calendar className="h-5 w-5 sm:h-6 sm:w-6" />
            </AvatarFallback>
          </Avatar>

          {/* Innhold - flex-1 for å ta opp tilgjengelig plass */}
          <div className="flex-1 min-w-0 space-y-1 sm:space-y-1.5">
            {/* Navn og status badge */}
            <div className="flex items-start gap-1.5 sm:gap-2 flex-wrap">
              <h3 className="text-sm sm:text-base font-semibold text-foreground truncate flex-1 min-w-0">
                {produksjon.navn}
              </h3>
              <Badge 
                variant={produksjon.publisert ? "default" : "outline"}
                className={`gap-1 shrink-0 text-xs ${
                  produksjon.publisert 
                    ? 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900 dark:text-green-100' 
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {produksjon.publisert ? (
                  <Eye className="h-3 w-3" />
                ) : (
                  <EyeOff className="h-3 w-3" />
                )}
                <span className="hidden xs:inline">
                  {produksjon.publisert ? 'Publisert' : 'Upublisert'}
                </span>
              </Badge>
            </div>

            {/* Metadata */}
            <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-muted-foreground flex-wrap">
              {/* Dato - full på sm+, kort på mobil */}
              <div className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0" />
                <span className="hidden sm:inline">{formatDato(produksjon.tid)}</span>
                <span className="sm:hidden">{formatKortDato(produksjon.tid)}</span>
              </div>

              {/* Antall personer */}
              {(produksjon.antall_personer ?? 0) > 0 && (
                <>
                  <span className="text-muted-foreground/50">•</span>
                  <div className="flex items-center gap-1">
                    <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0" />
                    <span>{produksjon.antall_personer}</span>
                  </div>
                </>
              )}
            </div>

            {/* Plassering (hvis den finnes) */}
            {produksjon.plassering && (
              <p className="text-xs text-muted-foreground truncate">
                📍 {produksjon.plassering}
              </p>
            )}
          </div>

          {/* Chevron - alltid synlig */}
          <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6 text-muted-foreground shrink-0 mt-0.5" />
        </div>
      </CardContent>
    </Card>
  );
};

export default ProductionCard;

